import { HttpClient }              from '@angular/common/http';
import {
  Inject,
  Injectable,
}                                  from '@angular/core';
import { ENVIRONMENT }             from '~app/app.constants';
import { withDateDeserialization } from '~app/core/interceptors';
import {
  Beneficiary,
  CreditorRemittanceInformationType,
  DUPLICATE_PAYMENT_DATES,
  DuplicatePayment,
  DuplicatePaymentRequest,
  PaymentAccount,
  PaymentData,
  SimpleClient,
}                                  from '~domain/types';
import {
  datesToStrings,
  removeEmptyStrings,
}                                  from '~utils/common';

@Injectable({
  providedIn: 'root',
})
export class PaymentApiService {

  constructor(
    @Inject(ENVIRONMENT)
    private readonly environment: Environment,
    private readonly http: HttpClient,
  ) {
  }

  fetchClients() {
    return this.http.get<SimpleClient[]>(`${this.environment.api}/clients-to-link`);
  }

  fetchClientBeneficiaries(clientId: string) {
    return this.http.get<Beneficiary[]>(`${this.environment.api}/clients/${clientId}/beneficiaries?global=false`);
  }

  fetchGlobalBeneficiaries(officeId: string) {
    return this.http.get<Beneficiary[]>(`${this.environment.api}/offices/${officeId}/beneficiaries`);
  }

  fetchAccounts() {
    return this.http.get<PaymentAccount[]>(`${this.environment.api}/payment-accounts`);
  }

  addPayment(paymentData: PaymentData) {
    const { clientId, ...payment } = paymentData;
    const payments = [this.createPaymentRequestBody(payment)];
    return this.http.post<void>(`${this.environment.api}/clients/${clientId}/batch/payments`, { payments });
  }

  updatePayment(paymentData: PaymentData) {
    const { id, ...payment } = paymentData;
    return this.http.put<void>(`${this.environment.api}/payments/${id}`, this.createPaymentRequestBody(payment));
  }

  updateBeneficiary(beneficiaryId: string, clientId: string, beneficiary: Beneficiary) {
    return this.http.put<void>(`${this.environment.api}/clients/${clientId}/beneficiaries/${beneficiaryId}`, beneficiary);
  }

  addBeneficiary(clientId: string, beneficiary: Beneficiary) {
    return this.http.post<void>(`${this.environment.api}/clients/${clientId}/beneficiaries`, beneficiary);
  }

  deleteBeneficiary(beneficiaryId: string, clientId: string) {
    return this.http.delete<void>(`${this.environment.api}/clients/${clientId}/beneficiaries/${beneficiaryId}`);
  }

  findDuplicatePayments(clientId: string, request: DuplicatePaymentRequest) {
    const body = removeEmptyStrings(datesToStrings(request));
    return this.http.post<DuplicatePayment[]>(
      `${this.environment.api}/clients/${clientId}/duplicate-payment-requests`,
      body,
      { context: withDateDeserialization(DUPLICATE_PAYMENT_DATES) },
    );
  }

  private createPaymentRequestBody(paymentData: PaymentData) {
    const payment = removeEmptyStrings(datesToStrings(paymentData));
    const remittanceInformationType = payment.remittanceInformation ? payment.remittanceInformationType : CreditorRemittanceInformationType.unstructured;
    return {
      ...payment,
      remittanceInformationType,
    };
  }
}
